import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Search, X, Users, Building2, GraduationCap, TrendingUp, Package, ShieldCheck, Wrench, Calendar, FileText } from 'lucide-react';

const ICONS = {
  users: Users,
  building: Building2,
  'graduation-cap': GraduationCap,
  'trending-up': TrendingUp,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
  calendar: Calendar,
  'file-text': FileText,
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd/Ctrl + K → open / ESC → close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    if (!open) { setQ(''); setData(null); setActiveIdx(0); }
  }, [open]);

  // Debounced fetch after 2+ chars
  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=5`);
        setData(r.data);
        setActiveIdx(0);
      } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(h);
  }, [q]);

  // Flatten results for keyboard navigation
  const flat = (data?.results || []).flatMap(g => g.results.map(r => ({ ...r, type: g.type })));

  const handleKey = (e) => {
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); selectResult(flat[activeIdx]); }
  };

  const selectResult = (r) => {
    if (!r) return;
    setOpen(false);
    navigate(r.url);
  };

  return (
    <>
      {/* Trigger button in nav */}
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs hover:bg-gray-50"
        style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
        data-testid="global-search-trigger"
        title="Search everything — ⌘K">
        <Search size={13} />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden md:inline text-[9px] px-1 py-0.5 rounded border" style={{ borderColor: '#E5E1DC', color: '#9CA3AF' }}>⌘K</kbd>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-[10vh] p-3" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[75vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-testid="global-search-modal">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b" style={{ borderColor: '#E5E1DC' }}>
              <Search size={16} style={{ color: '#9CA3AF' }} />
              <input
                ref={inputRef}
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Search people, outlets, SOPs, deals, vendors, assets…"
                className="flex-1 outline-none text-sm"
                data-testid="global-search-input"
              />
              {loading && <span className="text-[10px]" style={{ color: '#9CA3AF' }}>…</span>}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-100 rounded"><X size={14} style={{ color: '#6B7280' }} /></button>
            </div>

            <div className="overflow-y-auto flex-1">
              {q.trim().length < 2 && (
                <div className="p-6 text-center text-xs" style={{ color: '#9CA3AF' }}>
                  <p>Type at least 2 characters…</p>
                  <div className="mt-3 flex flex-wrap gap-1.5 justify-center text-[11px]">
                    <span style={{ color: '#6B7280' }}>Try:</span>
                    {['Dhiraj', 'Celeste', 'fire', 'JLR', 'onboarding'].map(s => (
                      <button key={s} onClick={() => setQ(s)} className="px-2 py-0.5 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#378ADD' }}>{s}</button>
                    ))}
                  </div>
                  <div className="mt-4 text-[10px]" style={{ color: '#D1D5DB' }}>
                    ↑↓ navigate · ↵ open · ESC close
                  </div>
                </div>
              )}

              {q.trim().length >= 2 && data && data.total === 0 && !loading && (
                <div className="p-6 text-center text-xs" style={{ color: '#9CA3AF' }}>
                  No results for "<strong style={{ color: '#1B2A4A' }}>{q}</strong>".
                </div>
              )}

              {data && data.results.map((group) => {
                const Icon = ICONS[group.icon] || FileText;
                return (
                  <div key={group.type} className="pb-1">
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 sticky top-0" style={{ color: '#9CA3AF', backgroundColor: '#F7F5F2' }}>
                      <Icon size={10} />
                      {group.label} · <span style={{ color: '#6B7280' }}>{group.results.length}</span>
                    </div>
                    {group.results.map((r) => {
                      const idx = flat.findIndex(f => f.id === r.id && f.type === group.type);
                      const active = idx === activeIdx;
                      return (
                        <button
                          key={r.id}
                          onClick={() => selectResult({ ...r, type: group.type })}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className="w-full text-left px-3 py-2 flex items-start gap-2 transition border-l-2"
                          style={{
                            backgroundColor: active ? '#F0F9FF' : 'transparent',
                            borderLeftColor: active ? '#378ADD' : 'transparent',
                          }}
                          data-testid={`search-result-${group.type}-${r.id}`}
                        >
                          <Icon size={13} className="mt-0.5 flex-shrink-0" style={{ color: active ? '#378ADD' : '#9CA3AF' }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{r.title || '—'}</p>
                            <p className="text-[10px] truncate" style={{ color: '#6B7280' }}>{r.subtitle}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {data && data.total > 0 && (
              <div className="border-t px-3 py-1.5 flex items-center justify-between text-[10px]" style={{ borderColor: '#E5E1DC', color: '#9CA3AF' }}>
                <span>{data.total} result{data.total === 1 ? '' : 's'}</span>
                <span>↑↓ navigate · ↵ open · ESC close</span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
